import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, PackagePlus, AlertTriangle, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, money } from '../../lib/api';
import PullToRefresh from '../../components/PullToRefresh';
import SwipeRow from '../../components/SwipeRow';
import ReceiveStockModal from '../../components/ReceiveStockModal';
import MergeProductModal from '../../components/MergeProductModal';

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [merging, setMerging] = useState(null);
  const negatives = products.filter((p) => !p.is_service && p.remaining_qty < 0);

  const load = () => adminApi.get('/admin/products').then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const saveNew = async (data) => {
    try { await adminApi.post('/admin/products', data); setAdding(false); load(); toast.success('Product added'); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const saveEdit = async (id, data) => {
    try { await adminApi.patch(`/admin/products/${id}`, data); setEditing(null); load(); toast.success('Saved'); } catch (e) { toast.error('Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await adminApi.delete(`/admin/products/${id}`); load(); toast.success('Deleted'); } catch (e) { toast.error('Failed'); }
  };

  return (
    <PullToRefresh onRefresh={load}>
    <div className="fade-in space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-semibold">Inventory</h1>
          <p className="text-sm text-[#6b7280] mt-1">{products.length} products · track costs, stock, and revenue</p>
        </div>
        <button onClick={() => setAdding(true)} className="btn-brand flex items-center gap-2" data-testid="add-product-btn">
          <Plus size={16} /> Add Product
        </button>
      </div>

      <NegativeStockBanner items={negatives} onReceive={setReceiving} />

      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-right px-4 py-3">Ordered</th>
              <th className="text-right px-4 py-3">Sold</th>
              <th className="text-right px-4 py-3">Remaining</th>
              <th className="text-right px-4 py-3">Net Cost</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-right px-4 py-3">Remaining $</th>
              <th className="text-right px-4 py-3">Revenue<br/><span className="text-[#6b7280] font-normal normal-case text-[10px]">grand / paid / unpaid</span></th>
              <th className="text-right px-4 py-3">Profit<br/><span className="text-[#6b7280] font-normal normal-case text-[10px]">grand / paid / unpaid</span></th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.product_id} className="border-t border-[#f3f3f3] hover:bg-[#fafafa]">
                <td className="px-4 py-3">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-[#6b7280] mono">{p.sku}</div>
                </td>
                <td className="text-right px-4 py-3 mono">{p.ordered_qty}</td>
                <td className="text-right px-4 py-3 mono text-[#6b7280]">{p.qty_sold || 0}</td>
                <td className={`text-right px-4 py-3 mono ${!p.is_service && p.remaining_qty <= 10 ? 'text-[#d92027] font-bold' : ''}`} data-testid={`remaining-${p.product_id}`}>{p.is_service ? <ServiceBadge /> : p.remaining_qty}</td>
                <td className="text-right px-4 py-3 mono">{money(p.net_cost_per_unit)}</td>
                <td className="text-right px-4 py-3 mono">{money(p.default_price)}</td>
                <td className="text-right px-4 py-3 mono">{money(p.remaining_net_value)}</td>
                <td className="text-right px-4 py-3 text-xs">
                  <div className="mono font-semibold text-[#0a0a0a]">{money(p.revenue_total)}</div>
                  <div className="text-green-700 mono">{money(p.revenue_paid)}</div>
                  <div className="text-[#d92027] mono">{money(p.revenue_unpaid)}</div>
                </td>
                <td className="text-right px-4 py-3 text-xs">
                  <div className="mono font-semibold text-[#0a0a0a]">{money(p.profit_total)}</div>
                  <div className="text-green-700 mono">{money(p.profit_paid)}</div>
                  <div className="text-[#d92027] mono">{money(p.profit_unpaid)}</div>
                </td>
                <td className="text-right px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    {!p.is_service && <button onClick={() => setReceiving(p)} title="Receive stock" className="p-1.5 hover:bg-white rounded text-green-700" data-testid={`receive-product-${p.product_id}`}><PackagePlus size={14} /></button>}
                    <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-white rounded" data-testid={`edit-product-${p.product_id}`}><Edit2 size={14} /></button>
                    <button onClick={() => del(p.product_id)} className="p-1.5 hover:bg-white rounded text-[#d92027]" data-testid={`delete-product-${p.product_id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && <div className="py-10 text-center text-sm text-[#6b7280]">No products yet.</div>}
      </div>

      {/* Mobile: card list */}
      <div className="md:hidden space-y-3">
        {products.length === 0 ? (
          <div className="card py-10 text-center text-sm text-[#6b7280]">No products yet.</div>
        ) : (
          products.map((p) => (
            <SwipeRow
              key={p.product_id}
              onDelete={() => del(p.product_id)}
              testid={`swipe-product-${p.product_id}`}
            >
              <div className="card p-4" data-testid={`mobile-product-${p.product_id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{p.name}</div>
                  {p.sku && <div className="text-xs text-[#6b7280] mono mt-0.5">{p.sku}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  {!p.is_service && <button onClick={() => setReceiving(p)} className="p-2 rounded-lg bg-green-50 text-green-700" data-testid={`m-receive-product-${p.product_id}`}><PackagePlus size={16} /></button>}
                  <button onClick={() => setEditing(p)} className="p-2 rounded-lg bg-[#fafafa] text-[#6b7280]" data-testid={`m-edit-product-${p.product_id}`}><Edit2 size={16} /></button>
                  <button onClick={() => del(p.product_id)} className="p-2 rounded-lg bg-[#fef2f2] text-[#d92027]" data-testid={`m-delete-product-${p.product_id}`}><Trash2 size={16} /></button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t border-[#f3f3f3]">
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Ordered</div>
                  <div className="mono font-semibold">{p.ordered_qty}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Sold</div>
                  <div className="mono font-semibold">{p.qty_sold || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Remaining</div>
                  <div className={`mono font-semibold ${!p.is_service && p.remaining_qty <= 10 ? 'text-[#d92027]' : ''}`}>{p.is_service ? <ServiceBadge /> : p.remaining_qty}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Cost</div>
                  <div className="mono">{money(p.net_cost_per_unit)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Price</div>
                  <div className="mono">{money(p.default_price)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Stock $</div>
                  <div className="mono">{money(p.remaining_net_value)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs mt-3 pt-3 border-t border-[#f3f3f3]">
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Revenue</div>
                  <div className="mono font-semibold">{money(p.revenue_total)}</div>
                  <div className="mono text-green-700 text-[11px]">{money(p.revenue_paid)} paid</div>
                  <div className="mono text-[#d92027] text-[11px]">{money(p.revenue_unpaid)} unpaid</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Profit</div>
                  <div className="mono font-semibold">{money(p.profit_total)}</div>
                  <div className="mono text-green-700 text-[11px]">{money(p.profit_paid)} paid</div>
                  <div className="mono text-[#d92027] text-[11px]">{money(p.profit_unpaid)} unpaid</div>
                </div>
              </div>
              </div>
            </SwipeRow>
          ))
        )}
      </div>

      {merging && (
        <MergeProductModal product={merging} products={products} onClose={() => setMerging(null)} onDone={() => { setMerging(null); load(); }} />
      )}
      {receiving && (
        <ReceiveStockModal product={receiving} onClose={() => setReceiving(null)} onDone={() => { setReceiving(null); load(); }} />
      )}
      {(adding || editing) && (
        <ProductModal product={editing} onClose={() => { setEditing(null); setAdding(false); }} onSave={editing ? (data) => saveEdit(editing.product_id, data) : saveNew} onMerge={editing ? () => { setMerging(editing); setEditing(null); } : null} />
      )}
    </div>
    </PullToRefresh>
  );
}

function ProductModal({ product, onClose, onSave, onMerge }) {
  const [form, setForm] = useState({
    name: product?.name || '',
    sku: product?.sku || '',
    description: product?.description || '',
    ordered_qty: product?.ordered_qty || 0,
    remaining_qty: product?.remaining_qty || 0,
    net_cost_per_unit: product?.net_cost_per_unit || 0,
    default_price: product?.default_price || 0,
    is_service: !!product?.is_service,
  });
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const save = () => {
    if (!product) return onSave(form);
    const { ordered_qty, remaining_qty, ...rest } = form;
    const changed = Object.fromEntries(Object.entries(rest).filter(([k, v]) => v !== (k === 'is_service' ? !!product[k] : product[k])));
    if (Object.keys(changed).length === 0) return onClose();
    onSave(changed);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
        <div className="p-6 border-b border-[#e7e7e7] flex items-center justify-between">
          <h2 className="text-xl font-semibold">{product ? 'Edit Product' : 'New Product'}</h2>
          <button onClick={onClose} data-testid="close-product-modal"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Name"><input className="input" value={form.name} onChange={(e) => upd('name', e.target.value)} data-testid="product-name-input" /></Field>
          <Field label="SKU"><input className="input" value={form.sku} onChange={(e) => upd('sku', e.target.value)} /></Field>
          <Field label="Description"><input className="input" value={form.description} onChange={(e) => upd('description', e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-4">
            {!product && !form.is_service && <>
              <Field label="Ordered Qty"><input type="number" className="input" value={form.ordered_qty} onChange={(e) => upd('ordered_qty', Number(e.target.value))} /></Field>
              <Field label="Opening Stock"><input type="number" className="input" value={form.remaining_qty} onChange={(e) => upd('remaining_qty', Number(e.target.value))} data-testid="product-opening-stock-input" /></Field>
            </>}
            <Field label="Net Cost / unit"><input type="number" step="0.01" className="input" value={form.net_cost_per_unit} onChange={(e) => upd('net_cost_per_unit', Number(e.target.value))} /></Field>
            <Field label="Default Price / unit"><input type="number" step="0.01" className="input" value={form.default_price} onChange={(e) => upd('default_price', Number(e.target.value))} /></Field>
          </div>
          <label className="flex items-start gap-3 p-3 rounded-lg border border-[#e7e7e7] cursor-pointer">
            <input type="checkbox" className="mt-1 accent-[#d92027]" checked={form.is_service} onChange={(e) => upd('is_service', e.target.checked)} data-testid="product-is-service-checkbox" />
            <div>
              <div className="text-sm font-semibold">Service (no stock tracking)</div>
              <div className="text-xs text-[#6b7280]">e.g. Delivery — never counted in stock or inventory value</div>
            </div>
          </label>
          {product && !form.is_service && <p className="text-xs text-[#6b7280]">Stock levels change only through “Receive stock” and invoices.</p>}
        </div>
        <div className="p-6 border-t border-[#e7e7e7] flex justify-end gap-2">
          {onMerge && <button onClick={onMerge} className="btn-ghost mr-auto flex items-center gap-1" data-testid="open-merge-btn"><GitMerge size={14} /> Merge into…</button>}
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} className="btn-brand" data-testid="save-product-btn">Save</button>
        </div>
      </div>
      <style>{`.input{width:100%;padding:9px 12px;border:1px solid #e7e7e7;border-radius:8px;font-size:14px;} .input:focus{outline:none;border-color:#d92027;box-shadow:0 0 0 2px #fde8e8;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-[#1f1f1f] uppercase tracking-wide mb-1">{label}</div>
      {children}
    </label>
  );
}

function ServiceBadge() {
  return <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase tracking-wide">Service</span>;
}

function NegativeStockBanner({ items, onReceive }) {
  if (!items.length) return null;
  return (
    <div className="card p-4 sm:p-5 border-l-4 border-l-[#d92027]" data-testid="negative-stock-banner">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={18} className="text-[#d92027]" />
        <h2 className="text-base font-semibold">Negative stock — {items.length} product{items.length === 1 ? '' : 's'}</h2>
      </div>
      <p className="text-xs text-[#6b7280] mb-3">More was sold than received. Record the missing receipt with its landed cost to fix it.</p>
      <div className="flex flex-wrap gap-2">
        {items.map((p) => (
          <button key={p.product_id} onClick={() => onReceive(p)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#fef2f2] hover:bg-[#fde8e8] text-sm" data-testid={`negative-receive-${p.product_id}`}>
            <span className="font-medium">{p.name}</span>
            <span className="mono font-bold text-[#d92027]">{p.remaining_qty}</span>
            <PackagePlus size={14} className="text-[#d92027]" />
          </button>
        ))}
      </div>
    </div>
  );
}
